// ═══════════════════════════════════════════════════════════════
// CULTIVATION ACTIONS TAB — Priority action queue
// Path: src/components/cultivation/CultivationActionsTab.jsx
// ═══════════════════════════════════════════════════════════════

import React, { useMemo } from "react";
import { AlertCircle, ArrowRight } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, ICON_SIZES } from "../../constants/appConfig";
import { Card } from "../ui/Card";
import { EngagementMeter, URGENCY_COLORS } from "../../utils/cultivationUtils";

export const CultivationActionsTab = ({ enriched, onMemberClick }) => {
  const { palette } = useApp();

  const actionQueue = useMemo(
    () =>
      enriched
        .filter((m) => !m.isDonor || m.nextAction.type === "steward")
        .sort((a, b) => {
          const urgencyOrder = { high: 0, medium: 1, low: 2 };
          const uDiff =
            (urgencyOrder[a.nextAction.urgency] || 2) -
            (urgencyOrder[b.nextAction.urgency] || 2);
          if (uDiff !== 0) return uDiff;
          return b.engagement.score - a.engagement.score;
        }),
    [enriched]
  );

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", width: "100%" }}>
      {/* Priority Summary Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 12,
          marginBottom: 24,
        }}
      >
        {["high", "medium", "low"].map((u) => {
          const count = actionQueue.filter(
            (m) => m.nextAction.urgency === u
          ).length;
          const color = URGENCY_COLORS[u];
          const label = {
            high: "High Priority",
            medium: "Medium Priority",
            low: "Low Priority",
          }[u];
          return (
            <Card
              key={u}
              style={{
                padding: 16,
                textAlign: "center",
                borderLeft: `4px solid ${color}`,
              }}
            >
              <div style={{ fontSize: "1.4rem", fontWeight: 700, color }}>
                {count}
              </div>
              <div style={{ fontSize: FONT_SIZES.xs, color: palette.text2 }}>
                {label}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Action Lists by Priority */}
      {["high", "medium", "low"].map((urgency) => {
        const items = actionQueue.filter(
          (m) => m.nextAction.urgency === urgency
        );
        if (items.length === 0) return null;
        const urgencyLabel = {
          high: "High Priority",
          medium: "Medium Priority",
          low: "Low Priority",
        }[urgency];
        const urgencyColor = URGENCY_COLORS[urgency];

        return (
          <Card key={urgency} style={{ marginBottom: 16 }}>
            <h3
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                margin: "0 0 16px",
                fontSize: FONT_SIZES.md,
                fontWeight: 600,
                color: urgencyColor,
                paddingBottom: 12,
                borderBottom: `2px solid ${urgencyColor}`,
              }}
            >
              <AlertCircle size={ICON_SIZES.md} /> {urgencyLabel} ({items.length})
            </h3>
            {items.map((m) => {
              const ActionIcon = m.nextAction.icon;
              return (
                <div
                  key={m.id}
                  onClick={() => onMemberClick(m)}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                    padding: "12px 14px",
                    borderRadius: 8,
                    cursor: "pointer",
                    marginBottom: 6,
                    border: `1px solid ${palette.cardBorder}`,
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = `${urgencyColor}06`;
                    e.currentTarget.style.borderColor = urgencyColor;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.borderColor = palette.cardBorder;
                  }}
                >
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      flexShrink: 0,
                      background: `${urgencyColor}12`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <ActionIcon size={ICON_SIZES.sm} style={{ color: urgencyColor }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                        marginBottom: 4,
                      }}
                    >
                      <span
                        style={{
                          fontSize: FONT_SIZES.sm,
                          fontWeight: 600,
                          color: palette.text1,
                        }}
                      >
                        {m.firstName} {m.lastName}
                      </span>
                      <EngagementMeter
                        score={m.engagement.score}
                        palette={palette}
                      />
                    </div>
                    <div
                      style={{
                        fontSize: FONT_SIZES.xs,
                        color: palette.text1,
                        marginBottom: 2,
                      }}
                    >
                      <ArrowRight
                        size={ICON_SIZES.xs}
                        style={{
                          verticalAlign: "middle",
                          marginRight: 4,
                          color: urgencyColor,
                        }}
                      />
                      {m.nextAction.action}
                    </div>
                    {m.engagement.activeContacts === 0 &&
                      m.engagement.totalContacts > 0 && (
                        <div
                          style={{
                            fontSize: "0.65rem",
                            color: palette.text3,
                            marginTop: 4,
                          }}
                        >
                          All {m.engagement.totalContacts} contacts have decayed
                          — re-engage
                        </div>
                      )}
                    {m.engagement.totalContacts === 0 && (
                      <div
                        style={{
                          fontSize: "0.65rem",
                          color: palette.text3,
                          marginTop: 4,
                        }}
                      >
                        No contact history
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </Card>
        );
      })}
    </div>
  );
};
